import {
  ParagraphOp,
  ParagraphSet,
  ParagraphTarget,
  PresentationModification,
} from "../types";

export interface ParagraphOpNormalizationStats {
  normalizedOps: number;
  droppedOps: number;
  touchedShapes: number;
}

export interface ParagraphSemanticsInferenceStats {
  inferredShapes: number;
  inferredBulletedShapes: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeParagraphTarget(value: unknown): ParagraphTarget | null {
  if (!isRecord(value) || typeof value.type !== "string") {
    return null;
  }

  if (value.type === "all") {
    return { type: "all" };
  }

  if (value.type === "indices" && Array.isArray(value.indices)) {
    const normalizedIndices = Array.from(
      new Set(
        value.indices
          .map((index) => (typeof index === "number" ? Math.floor(index) : NaN))
          .filter((index) => Number.isFinite(index) && index >= 0)
      )
    ).sort((a, b) => a - b);

    if (normalizedIndices.length === 0) {
      return null;
    }
    return {
      type: "indices",
      indices: normalizedIndices,
    };
  }

  return null;
}

function normalizeParagraphSet(value: unknown): ParagraphSet | null {
  if (!isRecord(value)) {
    return null;
  }

  // Passthrough the schema-shaped set: horizontalAlignment / indentLevel / font /
  // text / runs all flow to the host as-is. We no longer rebuild from an allowlist
  // (that path silently dropped per-paragraph font). Only bullets get host-safety
  // coercion below.
  const normalized: ParagraphSet = { ...(value as ParagraphSet) };

  if (isRecord(value.bullet)) {
    const bullet: NonNullable<ParagraphSet["bullet"]> = {
      ...(value.bullet as NonNullable<ParagraphSet["bullet"]>),
    };

    // bullet.style is a host no-op on modify and CRASHES the whole batch on create;
    // never forward it (the schema also omits it so the model should not emit it).
    delete bullet.style;

    if (bullet.type === "None") {
      // Preserve explicit hidden state when turning bullets off.
      if (bullet.visible === undefined) {
        bullet.visible = false;
      }
    } else if (
      (bullet.type === "Numbered" || bullet.type === "Unnumbered") &&
      bullet.visible === undefined
    ) {
      // Setting a concrete bullet type while bullets are hidden can trigger
      // host-level InvalidArgument.
      bullet.visible = true;
    }

    if (Object.keys(bullet).length > 0) {
      normalized.bullet = bullet;
    } else {
      delete normalized.bullet;
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : null;
}

function normalizeParagraphOp(value: unknown): ParagraphOp | null {
  if (!isRecord(value)) {
    return null;
  }

  const target = normalizeParagraphTarget(value.target);
  if (!target) {
    return null;
  }

  const set = normalizeParagraphSet(value.set);
  if (!set) {
    return null;
  }

  return { target, set };
}

const UNORDERED_LIST_PATTERN = /^[-*•]\s+(.+)$/;
const NUMBERED_LIST_PATTERN = /^(\d+)[.)]\s+(.+)$/;

type InferredListType = "unordered" | "numbered";

function inferListSemantics(text: string): { type: InferredListType; normalizedText: string } | null {
  const normalizedText = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalizedText.split("\n");
  const rewrittenLines: string[] = [];
  let inferredType: InferredListType | null = null;
  let listLineCount = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      rewrittenLines.push("");
      continue;
    }

    const unorderedMatch = trimmed.match(UNORDERED_LIST_PATTERN);
    const numberedMatch = trimmed.match(NUMBERED_LIST_PATTERN);
    if (!unorderedMatch && !numberedMatch) {
      return null;
    }

    const lineType: InferredListType = unorderedMatch ? "unordered" : "numbered";
    if (inferredType && inferredType !== lineType) {
      return null;
    }
    inferredType = lineType;

    const content = (unorderedMatch?.[1] || numberedMatch?.[2] || "").trim();
    if (!content) {
      return null;
    }
    rewrittenLines.push(content);
    listLineCount += 1;
  }

  if (!inferredType || listLineCount < 2) {
    return null;
  }

  return {
    type: inferredType,
    normalizedText: rewrittenLines.join("\r"),
  };
}

export function inferParagraphSemanticsForCreateShapes(
  modifications: PresentationModification
): ParagraphSemanticsInferenceStats {
  const stats: ParagraphSemanticsInferenceStats = {
    inferredShapes: 0,
    inferredBulletedShapes: 0,
  };

  for (const slide of modifications.slides || []) {
    for (const shape of slide.shapes || []) {
      if (shape.action !== "create") {
        continue;
      }
      if (Array.isArray(shape.paragraphOps) && shape.paragraphOps.length > 0) {
        continue;
      }
      if (shape.table || shape.chart || shape.imageUrl || shape.imageDataUri || shape.imageBase64) {
        continue;
      }
      if (typeof shape.text !== "string" || !shape.text.trim()) {
        continue;
      }

      const listInference = inferListSemantics(shape.text);
      if (!listInference) {
        continue;
      }

      shape.text = listInference.normalizedText;
      shape.paragraphOps = [
        {
          target: { type: "all" },
          set: {
            bullet: {
              type: listInference.type === "numbered" ? "Numbered" : "Unnumbered",
              visible: true,
            },
          },
        },
      ];
      stats.inferredShapes += 1;
      stats.inferredBulletedShapes += 1;
    }
  }

  return stats;
}

export function normalizeParagraphOpsInModifications(
  modifications: PresentationModification
): ParagraphOpNormalizationStats {
  const stats: ParagraphOpNormalizationStats = {
    normalizedOps: 0,
    droppedOps: 0,
    touchedShapes: 0,
  };

  for (const slide of modifications.slides || []) {
    for (const shape of slide.shapes || []) {
      if (!Array.isArray(shape.paragraphOps)) {
        continue;
      }

      const normalizedOps: ParagraphOp[] = [];
      let droppedOpsForShape = 0;
      for (const paragraphOp of shape.paragraphOps) {
        const normalizedOp = normalizeParagraphOp(paragraphOp);
        if (normalizedOp) {
          normalizedOps.push(normalizedOp);
        } else {
          droppedOpsForShape += 1;
        }
      }

      if (normalizedOps.length > 0) {
        shape.paragraphOps = normalizedOps;
        stats.normalizedOps += normalizedOps.length;
      } else {
        delete shape.paragraphOps;
      }
      if (droppedOpsForShape > 0 || normalizedOps.length > 0) {
        stats.touchedShapes += 1;
      }
      stats.droppedOps += droppedOpsForShape;
    }
  }

  return stats;
}
